import { InvalidUrlError } from '../../../common/errors/errors';
import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { FilterOperator } from '../../../common/types/gql-types';
import { CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { DEFAULT_MATCHED_CERTIFICATES_PAGE_SIZE } from '../components/MatchedCertificatesTable';
import { OrganizationCertificateDetailMapper } from '../mapper/OrganizationCertificateDetailMapper';
import { OrganizationCertificateMapper } from '../mapper/OrganizationCertificateMapper';
import { ReportCertificateMapper } from '../mapper/ReportCertificateMapper';
import { OrganizationCertificate } from '../models/OrganizationCertificate';
import { OrganizationCertificateDetail } from '../models/OrganizationCertificateDetail';
import { ReportCertificate } from '../models/ReportCertificate';

const MATCHED_CERTS_FRAGMENT = `
pageInfo {
  hasNextPage
  hasPreviousPage
  startCursor
  endCursor
}
edges {
  node {
    id
    report {
      id
      name
      project {
        id
        name
      }
    }
  }
}
totalCount
`;

// TODO(a/1204489651205943): Support sorters
export const GET_ORG_CERTS_QUERY = `
query(
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  $filters: OrganizationCertificatesFilter,
) {
  viewer {
    organization {
      certificates(
        first: $first,
        after: $after,
        last: $last,
        before: $before,
        filters: $filters,
      ) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            id
            subject
            issuer
            notAfter
            signatureAlgorithm {
              digest
            }
            reportCertificates {
              totalCount
            }
            publicKey {
              id
              shortDescription
            }
            fingerprint
          }
        }
        totalCount
      }
    }
  }
}
`;

export const GET_ORG_CERT_QUERY = `
query($certificateId: ID!, $firstReportCertificates: Int) {
  certificate: node(id: $certificateId) {
    ... on OrganizationCertificate {
      id
      subject
      issuer
      notAfter
      notBefore
      signatureAlgorithm {
        algorithm
        digest
        rsaPadding
      }
      publicKey {
        id
        shortDescription
        hasPrivateKey
      }
      isCa
      isSelfSigned
      encoded
      version
      serialNumber
      signature
      extensions {
        critical
        name
        value
      }
      reportCertificates(first: $firstReportCertificates) {
        ${MATCHED_CERTS_FRAGMENT}
      }
    }
  }
}
`;

export const GET_ORG_CERT_REPORT_CERTS_QUERY = `
query (
  $certificateId: ID!,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  ) {
  node(id: $certificateId) {
    ... on OrganizationCertificate {
      reportCertificates(
        first: $first,
        after: $after,
        last: $last,
        before: $before,
      ) {
        ${MATCHED_CERTS_FRAGMENT}
      }
    }
  }
}
`;

export class OrganizationCertificatesService extends BaseGQLService {
  private readonly mapper = new OrganizationCertificateMapper();
  private readonly certificateDetailMapper = new OrganizationCertificateDetailMapper();
  private readonly reportCertificateMapper = new ReportCertificateMapper();

  async getList(cursor: CursorInfo, filters: FilterOperator | null): Promise<PaginationResult<OrganizationCertificate>> {
    return this.query(
      GET_ORG_CERTS_QUERY,
      {
        ...this.mapCursorInfo(cursor),
        filters,
      },
      (data) => {
        const { edges, pageInfo, totalCount } = data.viewer.organization.certificates;
        return {
          data: edges.map(({ node }: any) => this.mapper.mapGQLModel(node)),
          pageInfo,
          totalCount,
        };
      }
    );
  }

  async getOrganizationCertificate(
    certificateId: number
  ): Promise<{ certificate: OrganizationCertificateDetail; initialMatchedCertificates: PaginationResult<ReportCertificate> }> {
    return this.query(
      GET_ORG_CERT_QUERY,
      {
        certificateId: this.gqlIdEncoder.encode(ModelType.OrganizationCertificate, certificateId),
        firstReportCertificates: DEFAULT_MATCHED_CERTIFICATES_PAGE_SIZE,
      },
      (data) => {
        if (!data.certificate) {
          throw new InvalidUrlError(`Invalid request: could not find OrganizationCertificate ${certificateId}`);
        }
        const { reportCertificates, ...certificate } = data.certificate;
        return {
          certificate: this.certificateDetailMapper.mapGQLModel(certificate),
          initialMatchedCertificates: {
            pageInfo: reportCertificates.pageInfo,
            totalCount: reportCertificates.totalCount,
            data: reportCertificates.edges.map((e: any) => this.reportCertificateMapper.mapGQLModel(e.node)),
          },
        };
      }
    );
  }

  async getMatchedCertificates(certificateId: number, cursor: CursorInfo): Promise<PaginationResult<ReportCertificate>> {
    return this.query(
      GET_ORG_CERT_REPORT_CERTS_QUERY,
      {
        certificateId: this.gqlIdEncoder.encode(ModelType.OrganizationCertificate, certificateId),
        ...this.mapCursorInfo(cursor),
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find OrganizationCertificate ${certificateId}`);
        }
        const { edges: certificates, pageInfo, totalCount } = data.node.reportCertificates;
        return {
          data: certificates.map((certificate: any) => this.reportCertificateMapper.mapGQLModel(certificate.node)),
          pageInfo,
          totalCount,
        };
      }
    );
  }
}
